import React, { memo, useCallback, useEffect, useRef } from 'react';

import { Box } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import {
  CASH_ACCOUNT_IDENTIFIER,
  CASH_APP_ACCOUNT_IDENTIFIER,
  PAYPAL_ACCOUNT_IDENTIFIER,
  VENMO_ACCOUNT_IDENTIFIER,
} from 'features/payment/constants';
import { CardTypes, IFraudPreventionValues, IPaymentDetails } from 'features/payment/types';
import { splitExpiry } from 'features/payment/utils';
import { useStoreContext } from 'features/store';
import { ExecutionResult } from 'graphql';
import { useIntl } from 'react-intl';

import { IServerOrder } from '@rbi-ctg/menu';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { ICommitOrderMutation, useGetRestaurantsQuery } from 'generated/graphql-gateway';
import { ICommitOrderInput, useCommitOrder } from 'hooks/commit-order';
import { useDistance } from 'hooks/geolocation/use-distance';
import { useCart } from 'hooks/use-cart';
import useErrorModal from 'hooks/use-error-modal';
import { useDismissAllAndNavigate } from 'navigation/hooks/use-dismiss-and-navigate';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderStatus, useOrderContext } from 'state/order';
import {
  GraphQLErrorCodes,
  getErrorDialogForGraphQLCode,
  parseGraphQLErrorCodes,
} from 'utils/errors';
import { logger } from 'utils/logger';
import { routes } from 'utils/routing';

import { CardButton } from './card-button';

type onCommitType = (input: ICommitOrderInput) => Promise<ExecutionResult<ICommitOrderMutation>>;

type onCommitErrorType = (error: any) => void;

interface IHandleCommitOrder {
  onCommit: onCommitType;
  onCommitError: onCommitErrorType;
  payment: {
    clearPaymentDetails(): void;
    paymentDetails: IPaymentDetails;
  };
  skipCoolingPeriod?: boolean;
}
const handleCommitOrder = ({
  onCommit,
  onCommitError,
  payment,
  skipCoolingPeriod = false,
}: IHandleCommitOrder) => {
  const { paymentDetails, clearPaymentDetails } = payment;
  const {
    accountIdentifier,
    creditType,
    fdAccessToken,
    fdNonce,
    fraudPreventionValues = {} as IFraudPreventionValues,
    applePayDetails,
    googlePayDetails,
    paypalDetails,
    venmoDetails,
    cashappDetails,
  } = paymentDetails;

  let commitInput: ICommitOrderInput;

  // TODO refactor: this code should not be much different than non pickup orders but is a landmine when adding new payment methods
  if (accountIdentifier === CASH_ACCOUNT_IDENTIFIER) {
    commitInput = {
      creditType: CASH_ACCOUNT_IDENTIFIER,
      skipCoolingPeriod,
      payment: {
        cashPayment: true,
        fullName: fraudPreventionValues.fullName,
      },
    };
  } else if (accountIdentifier === PAYPAL_ACCOUNT_IDENTIFIER) {
    commitInput = {
      creditType: PAYPAL_ACCOUNT_IDENTIFIER,
      skipCoolingPeriod,
      paypalDetails,
    };
  } else if (accountIdentifier === VENMO_ACCOUNT_IDENTIFIER) {
    commitInput = {
      creditType: VENMO_ACCOUNT_IDENTIFIER,
      skipCoolingPeriod,
      venmoDetails,
    };
  } else if (accountIdentifier === CASH_APP_ACCOUNT_IDENTIFIER) {
    commitInput = {
      creditType: CASH_APP_ACCOUNT_IDENTIFIER,
      skipCoolingPeriod,
      payment: {
        fullName: '',
        cashAppInput: {
          fdAccessToken: cashappDetails!.fdAccessToken,
          fdNonce: cashappDetails!.fdNonce,
        },
      },
    };
  } else {
    const { expiryMonth, expiryYear } = splitExpiry(fraudPreventionValues.expiry ?? '');

    commitInput = {
      applePayDetails,
      creditType,
      fdAccountId: accountIdentifier,
      fullName: fraudPreventionValues.fullName,
      payment: {
        billingAddress: fraudPreventionValues.billingAddress,
        ccMetadata: {
          ccBin: fraudPreventionValues.ccBin,
          ccExpiryMonth: expiryMonth,
          ccExpiryYear: expiryYear,
          ccLast4: fraudPreventionValues.ccLast4,
        },
        firstDataInput: {
          accountIdentifier,
          fdAccessToken,
          fdNonce,
        },
        fullName: fraudPreventionValues.fullName,
      },
      googlePayDetails,
      skipCoolingPeriod,
    };
  }

  return onCommit(commitInput)
    .then(() => {
      clearPaymentDetails();
    })
    .catch(onCommitError);
};

const PickupCard = ({
  serverOrder,
  needConfirm,
  setNeedConfirmation,
  setProcessingPayment,
}: {
  confirmed?: boolean;
  serverOrder: IServerOrder;
  needConfirm?: boolean;
  setNeedConfirmation?: (value: boolean) => void;
  setProcessingPayment?: (value: boolean) => void;
}) => {
  const { formatMessage } = useIntl();
  const { onCommitSuccess } = useOrderContext();
  const { emptyCart } = useCart();
  const dismissAndNavigate = useDismissAllAndNavigate();

  const { store: restaurant } = useStoreContext();

  const distanceToStore = useDistance({
    restaurant,
    shouldUseUserCoordinatesIfAvailable: true,
  });

  const { commitOrder, loading, orderStatus, commitResult } = useCommitOrder({
    rbiOrderId: serverOrder.rbiOrderId,
    distanceToStore,
  });

  const { data: restaurantsData, loading: loadingRestaurant } = useGetRestaurantsQuery({
    variables: {
      input: {
        first: 1,
        coordinates: {
          userLat: serverOrder.cart.storeDetails.latitude,
          userLng: serverOrder.cart.storeDetails.longitude,
          searchRadius: 20,
        },
      },
    },
  });

  const {
    clearPaymentDetails,
    paymentDetails,
    loading: paymentDetailsLoading,
  } = usePaymentContext();
  const onCommitError = (err: any) => {
    throw err;
  };

  const onCommitOrderSkipCooldown = useCallback(() => {
    const payment = {
      clearPaymentDetails,
      paymentDetails,
    };

    handleCommitOrder({
      onCommit: commitOrder,
      onCommitError,
      payment,
      skipCoolingPeriod: true,
    }).then(() => {
      onCommitSuccess(serverOrder);
      return emptyCart();
    });
  }, [clearPaymentDetails, commitOrder, emptyCart, onCommitSuccess, paymentDetails, serverOrder]);

  const onCommitOrder = useCallback(async () => {
    let cleanPaymentDetails = paymentDetails;

    // Cleaning Google / Apple Pay details, edge case fix for Curbside + Reloading prepaid flow
    if (
      paymentDetails.accountIdentifier !== CardTypes.GOOGLE_PAY &&
      paymentDetails.accountIdentifier !== CardTypes.APPLE_PAY
    ) {
      cleanPaymentDetails = {
        ...paymentDetails,
        applePayDetails: undefined,
        googlePayDetails: undefined,
      };
    }

    const payment = {
      clearPaymentDetails,
      paymentDetails: cleanPaymentDetails,
    };

    logger.info({
      message: '[INFO] Order Confirmation - Payment Details',
      paymentDetails: cleanPaymentDetails,
    });

    await handleCommitOrder({ onCommit: commitOrder, onCommitError, payment });
    onCommitSuccess(serverOrder);
    return emptyCart();
  }, [clearPaymentDetails, commitOrder, emptyCart, onCommitSuccess, paymentDetails, serverOrder]);

  const onCancelModal = () => dismissAndNavigate(routes.orders);
  const [OrderCooldownErrorDialog, openOrderCooldownErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Order Commit Error',
  });

  const orderIsLoading =
    loading || paymentDetailsLoading || orderStatus === OrderStatus.INSERT_REQUESTED;

  const enableCheckoutAnimation = useFlag(LaunchDarklyFlag.ENABLE_CHECKOUT_ANIMATION);
  const isOrderStatusFailureHandledRef = useRef(false);

  useEffect(() => {
    if (!commitResult.error) {
      return;
    }

    const [parsedGqlError] = parseGraphQLErrorCodes(commitResult.error);
    if (!parsedGqlError) {
      return;
    }

    const mappedErrorCode = getErrorDialogForGraphQLCode(parsedGqlError, formatMessage);
    const isCommitOrderCoolingPeriodError =
      parsedGqlError.errorCode === GraphQLErrorCodes.COMMIT_ORDER_COOLING_PERIOD;

    if (
      mappedErrorCode &&
      !isOrderStatusFailureHandledRef.current &&
      isCommitOrderCoolingPeriodError
    ) {
      isOrderStatusFailureHandledRef.current = true;
      openOrderCooldownErrorDialog({
        body: formatMessage({ id: 'errorDialogCooldownMessage' }),
        title: formatMessage({ id: 'errorDialogCooldownTitle' }),
        message: formatMessage({ id: 'errorDialogCooldownMessage' }),
        dismissText: formatMessage({ id: 'viewRecentOrders' }),
        confirmationText: formatMessage({ id: 'placeOrder' }),
        onDismiss: () => onCancelModal(),
        onConfirm: () => onCommitOrderSkipCooldown(),
      });
    }
  }, [
    commitResult,
    formatMessage,
    onCancelModal,
    onCommitOrderSkipCooldown,
    openOrderCooldownErrorDialog,
  ]);

  return (
    <>
      <Box testID="pickup-card">
        <VisuallyHidden
          role="alert"
          accessibilityLabel={formatMessage(
            { id: 'pickUpConfirmationScreenReaderInstructions' },
            {
              screenReaderButtonName: 'imHere',
            }
          )}
        />
      </Box>
      {!loadingRestaurant && !!restaurantsData?.restaurants?.nodes[0] && (
        <>
          {needConfirm && (
            <CardButton
              isLoading={orderIsLoading}
              onCommit={() => {
                if (enableCheckoutAnimation && setProcessingPayment) {
                  setProcessingPayment(true);
                }
                if (enableCheckoutAnimation && setNeedConfirmation) {
                  setNeedConfirmation(false);
                }
                onCommitOrder();
              }}
              orderStatus={orderStatus}
              restaurant={{
                ...restaurantsData?.restaurants?.nodes[0],
                // TODO: Remove this, from the backend and from our GQL schema
                isOpenByServiceMode: {
                  curbside: false,
                  driveThru: false,
                  delivery: false,
                  diningRoom: false,
                  mobileOrderDriveThru: false,
                },
              }}
            />
          )}
        </>
      )}

      <OrderCooldownErrorDialog />
    </>
  );
};

export default memo(PickupCard);
